import { differenceInCalendarDays, format } from 'date-fns';
import { Bot, Loader2, User } from 'lucide-react';
import { type ReactNode, type Ref, useEffect, useRef } from 'react';

import { cn } from '@/lib/shadcn/utils';
import type { CandidateJobApi } from '@/modules/candidates/types';
import { PrescreenLinkSharedBadge } from '@/modules/hiring-manager/components/PrescreenLinkSharedBadge';
import { StatusChip } from '@/modules/hiring-manager/components/StatusChip';
import type {
  CandidateStage,
  CandidateStageQuery,
} from '@/modules/hiring-manager/hooks/useCandidatePipelineQuery';
import {
  candidateCurrentRole,
  candidateFullName,
  candidateStatusLabels,
} from '@/modules/hiring-manager/utils/candidateDisplay';
import { Checkbox } from '@/modules/ui/components/checkbox/Checkbox';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import { failureReason, reasonCopy } from '@/pages/hm/messages/outreachThread';
import { getInitials } from '@/utils/getInitials';

import type { StepOwner } from './agentSteps';
import {
  currentCompanyMonths,
  experienceMonths,
  formatStageAge,
  formatTenure,
  formatYears,
} from './candidateFacts';
import type { StageInfo } from './candidateStages';
import { InboundBadge } from './InboundBadge';
import { ShowMoreCandidates } from './ShowMoreCandidates';

/** After this many days a waiting candidate is flagged amber. */
const LATE_AFTER_DAYS = 7;

/**
 * The Candidate column stays put while the rest of the table scrolls
 * sideways, so each row still says whose it is. Its cells need an opaque
 * background (rows pass theirs down) to hide what slides under them, and
 * cast a shadow once something has (faded in by `[data-pinned-scroll]` in
 * src/index.css). Full-width rows (stage headers, notices, show more) pin
 * their content to the visible frame the same way: `cqw` is the scroll
 * frame's width, not the table's.
 */
const stickyColumn =
  'sticky left-0 z-10 after:pointer-events-none after:absolute after:inset-y-0 after:left-full after:w-2 after:bg-linear-to-r after:from-neutral-900/8 after:opacity-0';

/** Pipeline statuses past the prescreen, shown with a green dot. */
const doneStatuses: CandidateJobApi['status'][] = [
  'Prescreened',
  'AiPrescreened',
  'AssessmentCompleted',
  'ReadyForSubmission',
];

export interface CandidateTableGroup extends StageInfo {
  query: CandidateStageQuery;
  /** Who acts next on the group; absent where the customer cannot change it. */
  owner?: StepOwner;
  /** A quiet aside after the hint in the group header. */
  note?: string;
  /** Group-wide actions, full width right under the group header. */
  notices?: ReactNode;
}

/**
 * Checkboxes for picking people to contact, on manual-outreach jobs: one per
 * contactable row and one in the header for all of them.
 */
export interface CandidateTableSelection {
  /** Rows without a checkbox keep its space, so names stay aligned. */
  hasCheckbox: (candidateJob: CandidateJobApi) => boolean;
  isSelected: (candidateJob: CandidateJobApi) => boolean;
  /** Finlay is writing this person's draft: the box is locked meanwhile. */
  isDrafting: (candidateJob: CandidateJobApi) => boolean;
  onToggle: (candidateJob: CandidateJobApi) => void;
  /** Ticks or clears every row given, from the header checkbox. */
  onSetSelected: (candidateJobs: CandidateJobApi[], selected: boolean) => void;
}

export interface CandidateTableProps {
  groups: CandidateTableGroup[];
  /** Group headers only help when more than one stage is in view. */
  showGroupHeaders: boolean;
  /** Stage titles left out because nobody is in them. */
  emptyStageTitles: string[];
  /** The candidate open in the side panel. */
  openCandidateId: string | undefined;
  onOpen: (candidateJob: CandidateJobApi) => void;
  selection?: CandidateTableSelection;
  /** A trailing column with one action per row; absent, there is no column. */
  rowAction?: (candidateJob: CandidateJobApi) => ReactNode;
  /**
   * Say why a first message failed. Only on jobs sent from the customer's
   * own LinkedIn: on Finlay's staff accounts the reason is not theirs to fix.
   */
  showFailureReasons: boolean;
}

/**
 * The job's pipeline as one table, grouped by stage in pipeline order, each
 * group paginated on its own. Clicking a row opens it in the side panel; the
 * profile columns give way to the panel while it is open.
 */
export function CandidateTable({
  groups,
  showGroupHeaders,
  emptyStageTitles,
  openCandidateId,
  onOpen,
  selection,
  rowAction,
  showFailureReasons,
}: CandidateTableProps) {
  const showProfile = openCandidateId === undefined;
  const columnCount = (showProfile ? 8 : 5) + (rowAction ? 1 : 0);
  const selectable = selection
    ? groups
        .flatMap((group) => group.query.candidates ?? [])
        .filter(
          (candidateJob) =>
            selection.hasCheckbox(candidateJob) &&
            !selection.isDrafting(candidateJob),
        )
    : [];
  const selectedCount = selectable.filter((candidateJob) =>
    selection?.isSelected(candidateJob),
  ).length;
  const isAllSelected =
    selectedCount > 0 && selectedCount === selectable.length;

  // Keyboard focus scrolled into view stops right of the pinned column
  // rather than under it.
  const frameRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef<HTMLTableCellElement>(null);
  useEffect(() => {
    const frame = frameRef.current;
    const pinned = pinnedRef.current;
    if (!frame || !pinned) return;
    const observer = new ResizeObserver(() => {
      frame.style.scrollPaddingLeft = `${pinned.offsetWidth}px`;
    });
    observer.observe(pinned);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="flex flex-col gap-2">
      {/* Overscroll is contained so a trackpad swipe past the left edge
          does not go back a page. */}
      <div className="relative" data-pinned-scroll>
        <div
          className="@container isolate overflow-x-auto overscroll-x-contain rounded-[10px] border"
          data-pinned-scroll-frame
          ref={frameRef}
        >
          {/* Separate borders, drawn per cell: collapsed ones belong to the
            table, so the sticky cells would paint over them. */}
          <table className="w-full border-separate border-spacing-0 text-[13px]">
            <thead className="bg-neutral-50 text-xs text-neutral-500">
              <tr>
                <HeaderCell
                  className={cn(stickyColumn, 'w-full bg-neutral-50')}
                  pinned
                  ref={pinnedRef}
                >
                  <span className="flex items-center gap-3">
                    {selection && (
                      <Checkbox
                        aria-label="Select all"
                        checked={
                          isAllSelected
                            ? true
                            : selectedCount > 0
                              ? 'indeterminate'
                              : false
                        }
                        disabled={selectable.length === 0}
                        onCheckedChange={() =>
                          selection.onSetSelected(selectable, !isAllSelected)
                        }
                      />
                    )}
                    Candidate
                  </span>
                </HeaderCell>
                <HeaderCell>Title</HeaderCell>
                <HeaderCell>Company</HeaderCell>
                <HeaderCell className="w-[136px] sm:w-[170px]">
                  Status
                </HeaderCell>
                <HeaderCell className="hidden w-[84px] sm:table-cell">
                  In stage
                </HeaderCell>
                {showProfile && (
                  <>
                    <HeaderCell className="hidden w-[150px] lg:table-cell">
                      Location
                    </HeaderCell>
                    <HeaderCell className="hidden w-[96px] lg:table-cell">
                      Experience
                    </HeaderCell>
                    <HeaderCell className="hidden w-[140px] xl:table-cell">
                      At current company
                    </HeaderCell>
                  </>
                )}
                {rowAction && (
                  <HeaderCell className="w-px">
                    <span className="sr-only">Actions</span>
                  </HeaderCell>
                )}
              </tr>
            </thead>
            {groups.map((group) => (
              <tbody aria-labelledby={`stage-${group.key}`} key={group.key}>
                {showGroupHeaders && (
                  <tr className="*:border-t">
                    <th
                      className="px-3 pt-3.5 pb-2 text-left font-normal"
                      colSpan={columnCount}
                      scope="colgroup"
                    >
                      <div className="sticky left-3 flex min-h-7 w-[calc(100cqw-1.5rem)] items-center gap-2">
                        <h2
                          className="text-[13px] leading-[18px] font-semibold"
                          id={`stage-${group.key}`}
                        >
                          {group.title}
                        </h2>
                        {group.query.totalCount !== undefined && (
                          <span className="font-medium text-neutral-500 tabular-nums">
                            {group.query.totalCount}
                          </span>
                        )}
                        {group.owner && <OwnerBadge owner={group.owner} />}
                        {group.hint && (
                          <span className="hidden min-w-0 truncate text-neutral-500 sm:inline">
                            {group.hint}
                          </span>
                        )}
                        {group.note && (
                          <span className="shrink-0 text-neutral-500">
                            · {group.note}
                          </span>
                        )}
                      </div>
                    </th>
                  </tr>
                )}
                {group.notices && (
                  <tr>
                    <td className="p-0" colSpan={columnCount}>
                      <div className="sticky left-0 w-[100cqw]">
                        {group.notices}
                      </div>
                    </td>
                  </tr>
                )}
                {(group.query.candidates ?? []).map((candidateJob) => (
                  <CandidateTableRow
                    candidateJob={candidateJob}
                    isOpen={candidateJob.candidate.id === openCandidateId}
                    key={candidateJob.id}
                    onOpen={onOpen}
                    rowAction={rowAction}
                    selection={selection}
                    showFailureReason={showFailureReasons}
                    showProfile={showProfile}
                    stage={group.key}
                  />
                ))}
                {group.query.hasNextPage && (
                  <tr className="*:border-t">
                    <td className="h-10 px-3" colSpan={columnCount}>
                      <div className="sticky left-3 w-fit">
                        <ShowMoreCandidates stageQuery={group.query} />
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            ))}
          </table>
        </div>
        {/* More columns wait past the right edge while this shows. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-px right-px w-6 rounded-r-[9px] bg-linear-to-l from-white opacity-0"
          data-pinned-scroll-fade
        />
      </div>
      {emptyStageTitles.length > 0 && (
        <p className="text-right text-xs text-neutral-500">
          No one in {emptyStageTitles.join(', ')} right now.
        </p>
      )}
    </div>
  );
}

function OwnerBadge({ owner }: { owner: StepOwner }) {
  const isFinlay = owner === 'finlay';
  const Icon = isFinlay ? Bot : User;
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-1 rounded-full pr-2 pl-1.5 text-xs font-medium',
        isFinlay
          ? 'bg-secondary-150 text-primary'
          : 'bg-muted text-neutral-600',
      )}
    >
      <Icon aria-hidden className="size-3" />
      <span className="sr-only">Handled by </span>
      {isFinlay ? 'Finlay' : 'You'}
    </span>
  );
}

function HeaderCell({
  className,
  children,
  pinned,
  ref,
}: {
  className?: string;
  children: ReactNode;
  pinned?: boolean;
  ref?: Ref<HTMLTableCellElement>;
}) {
  return (
    <th
      data-pinned={pinned || undefined}
      ref={ref}
      className={cn(
        'px-3 py-2.5 text-left font-medium whitespace-nowrap',
        className,
      )}
      scope="col"
    >
      {children}
    </th>
  );
}

function CandidateTableRow({
  candidateJob,
  isOpen,
  onOpen,
  rowAction,
  selection,
  showFailureReason,
  showProfile,
  stage,
}: {
  candidateJob: CandidateJobApi;
  isOpen: boolean;
  onOpen: (candidateJob: CandidateJobApi) => void;
  rowAction?: CandidateTableProps['rowAction'];
  selection?: CandidateTableSelection;
  showFailureReason: boolean;
  showProfile: boolean;
  stage: CandidateStage;
}) {
  const name = candidateFullName(candidateJob);
  const role = candidateCurrentRole(candidateJob);
  const experiences = candidateJob.candidate.workExperiences ?? [];
  const experience = experienceMonths(experiences);
  const tenure = currentCompanyMonths(experiences);
  const stageAge = candidateJob.stateEnteredAt
    ? formatStageAge(candidateJob.stateEnteredAt)
    : undefined;
  const isDrafting = selection?.isDrafting(candidateJob) ?? false;

  // Closing the panel (showProfile again) hands focus back to the row it
  // showed, so a keyboard user keeps their place in the table.
  const nameRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(isOpen);
  useEffect(() => {
    if (wasOpen.current && !isOpen && showProfile) nameRef.current?.focus();
    wasOpen.current = isOpen;
  }, [isOpen, showProfile]);

  // Stepping with Previous/Next (or a shared link) keeps the open row in
  // view. No smooth scroll: it follows a keypress.
  const rowRef = useRef<HTMLTableRowElement>(null);
  useEffect(() => {
    if (isOpen) rowRef.current?.scrollIntoView({ block: 'nearest' });
  }, [isOpen]);

  return (
    <tr
      className={cn(
        'cursor-pointer transition-colors duration-100 *:border-t',
        isOpen
          ? 'bg-secondary-150'
          : 'hover:bg-neutral-50 active:bg-neutral-100',
        !isOpen &&
          (selection?.isSelected(candidateJob)
            ? // secondary-150 at 60%, made opaque for the sticky name cell.
              'bg-[color-mix(in_srgb,var(--color-secondary-150)_60%,white)]'
            : 'bg-white'),
      )}
      onClick={() => onOpen(candidateJob)}
      ref={rowRef}
    >
      {/* The open row's edge bar sits on this cell, so it marks the row on a
          pale screen where the tint alone does not. */}
      <td
        data-pinned
        className={cn(
          stickyColumn,
          'bg-inherit px-3 py-2',
          isOpen && 'shadow-[inset_2px_0_0_var(--color-primary)]',
        )}
      >
        <div className="flex items-center gap-3">
          {selection && (
            // The box toggles selection only; the rest of the row opens the panel.
            <span
              className="flex size-4 shrink-0"
              onClick={(event) => event.stopPropagation()}
            >
              {selection.hasCheckbox(candidateJob) && (
                <Checkbox
                  aria-label={`Select ${name}`}
                  checked={selection.isSelected(candidateJob)}
                  disabled={isDrafting}
                  onCheckedChange={() => selection.onToggle(candidateJob)}
                />
              )}
            </span>
          )}
          <span className="bg-muted text-muted-foreground grid size-7 shrink-0 place-items-center rounded-full text-[10px] font-semibold tracking-wide">
            {getInitials(name)}
          </span>
          <span className="flex items-center gap-2">
            {/* Keyboard and screen-reader way into the row's panel. */}
            <button
              aria-expanded={isOpen}
              ref={nameRef}
              className="focus-visible:ring-ring/50 cursor-pointer rounded-sm text-left font-medium whitespace-nowrap outline-none focus-visible:ring-[3px]"
              type="button"
            >
              {name}
            </button>
            {/* No room beside the name on a phone. */}
            <span className="hidden shrink-0 gap-2 sm:inline-flex">
              <InboundBadge origin={candidateJob.origin} />
              <PrescreenLinkSharedBadge
                sharedPrescreenLinkType={candidateJob.sharedPrescreenLinkType}
                status={candidateJob.status}
              />
            </span>
          </span>
        </div>
      </td>
      {/* Never wrapped or truncated: the name, title and company are what
          tell people apart, so they show in full on one line and the table
          scrolls sideways when the page is narrower (a phone, or the side
          panel is open). */}
      <td className="px-3 py-2 whitespace-nowrap text-neutral-600">
        {role.title || <Missing />}
      </td>
      <td className="px-3 py-2 whitespace-nowrap text-neutral-600">
        {role.company || <Missing />}
      </td>
      <td className="px-3 py-2">
        <CandidateStatus
          candidateJob={candidateJob}
          isDrafting={isDrafting}
          showFailureReason={showFailureReason}
          stage={stage}
        />
      </td>
      <td className="hidden px-3 py-2 text-neutral-600 tabular-nums sm:table-cell">
        {stageAge ?? <Missing />}
      </td>
      {showProfile && (
        <>
          <td className="hidden px-3 py-2 text-neutral-600 lg:table-cell">
            <span className="block max-w-[150px] truncate">
              {candidateJob.candidate.location || <Missing />}
            </span>
          </td>
          <td className="hidden px-3 py-2 text-neutral-600 tabular-nums lg:table-cell">
            {experience === undefined ? <Missing /> : formatYears(experience)}
          </td>
          <td className="hidden px-3 py-2 text-neutral-600 tabular-nums xl:table-cell">
            {tenure === undefined ? <Missing /> : formatTenure(tenure)}
          </td>
        </>
      )}
      {rowAction && (
        // Row actions act on their own; the rest of the row opens the panel.
        // On a phone they are a sideways scroll away, past the name.
        <td
          className="px-3 py-2 text-right whitespace-nowrap"
          onClick={(event) => event.stopPropagation()}
        >
          {rowAction(candidateJob)}
        </td>
      )}
    </tr>
  );
}

function Missing() {
  return (
    <span aria-label="Unknown" className="text-neutral-400">
      —
    </span>
  );
}

/**
 * What is happening with the candidate and who acts next, worded for the
 * stage: the message in outreach, the prescreen after it, the wait on the
 * hiring manager, the interview.
 */
export function CandidateStatus({
  candidateJob,
  stage,
  isDrafting = false,
  showFailureReason,
}: {
  candidateJob: CandidateJobApi;
  stage: CandidateStage;
  isDrafting?: boolean;
  /** See `CandidateTableProps.showFailureReasons`. */
  showFailureReason: boolean;
}) {
  const label = candidateStatusLabels[candidateJob.status];

  if (isDrafting) {
    return (
      <span className="inline-flex h-6 items-center gap-1.5 text-xs font-medium whitespace-nowrap text-neutral-500">
        <Loader2 className="size-3.5 animate-spin" />
        Drafting…
      </span>
    );
  }

  switch (stage) {
    case 'sourced':
    case 'shortlisted':
      return <StatusChip tone="neutral">{label}</StatusChip>;
    case 'outreach': {
      const message = candidateJob.reachOutMessage ?? undefined;
      if (message?.status === 'Draft') {
        return <StatusChip tone="draft">Draft · not sent</StatusChip>;
      }
      if (message?.status === 'Pending') {
        return <StatusChip tone="neutral">Queued · sending</StatusChip>;
      }
      if (message?.status === 'Failed') {
        return showFailureReason ? (
          <FailedMessageChip message={message} />
        ) : (
          <StatusChip tone="failed">Failed</StatusChip>
        );
      }
      return <StatusChip tone="neutral">{label}</StatusChip>;
    }
    case 'prescreening':
      return (
        <StatusChip
          tone={doneStatuses.includes(candidateJob.status) ? 'done' : 'neutral'}
        >
          {label}
        </StatusChip>
      );
    case 'waiting': {
      const daysWaiting = candidateJob.stateEnteredAt
        ? differenceInCalendarDays(
            new Date(),
            new Date(candidateJob.stateEnteredAt),
          )
        : undefined;
      return daysWaiting !== undefined && daysWaiting > LATE_AFTER_DAYS ? (
        <StatusChip tone="draft">Waiting {daysWaiting}d</StatusChip>
      ) : (
        <StatusChip tone="decision">Your decision</StatusChip>
      );
    }
    case 'interviewing': {
      const interview = candidateJob.upcomingInterview;
      return (
        <StatusChip tone="neutral">
          {interview
            ? `Interview ${format(new Date(interview.startsAt), 'EEE d MMM')}`
            : candidateJob.status === 'HiringManagerApproved'
              ? 'Approved'
              : 'Interviewing'}
        </StatusChip>
      );
    }
    case 'hired':
      return <StatusChip tone="done">Hired</StatusChip>;
    case 'passed':
      return <StatusChip tone="neutral">Passed</StatusChip>;
  }
}

/**
 * Why the first message did not go out, in a few words, with the full
 * explanation on hover or focus. What can be fixed is red; what only waits
 * or cannot be helped is grey.
 */
function FailedMessageChip({
  message,
}: {
  message: NonNullable<CandidateJobApi['reachOutMessage']>;
}) {
  const copy = reasonCopy[failureReason(message)];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className="focus-visible:ring-ring/50 max-w-full cursor-default rounded-full outline-none focus-visible:ring-[3px]"
          type="button"
        >
          <StatusChip tone={copy.tone === 'warn' ? 'failed' : 'neutral'}>
            {copy.label}
          </StatusChip>
        </button>
      </TooltipTrigger>
      <TooltipContent
        className="flex max-w-[280px] flex-col gap-0.5 text-left"
        variant="dark"
      >
        <span className="font-semibold">{copy.title}</span>
        <span className="opacity-80">{copy.hint}</span>
      </TooltipContent>
    </Tooltip>
  );
}
